import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Copy, Pencil, Trash2 } from 'lucide-react'
import { deleteRole } from '@/api/services/roles.service'
import type { Role } from '@/types'
import { ConfirmDialog, Modal } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import {
  TableRowActionsMenu,
  TableRowActionsMenuDivider,
  TableRowActionsMenuItem,
} from '@/components/common/TableRowActionsMenu'
import {
  DataTable,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
} from '@/components/common/DataTable'
import { usePermissions } from '@/hooks/usePermissions'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { CloneRoleModal } from './CloneRoleModal'
import { EditRoleForm } from './EditRoleForm'
import { rolesQueryKeys } from '../hooks/useRoles'
import { QueryErrorState } from '@/components/common/QueryErrorState'

export interface RoleListProps {
  roles: Role[]
  isLoading: boolean
  error: Error | null
  /** Re-runs the query behind this list from the error state's retry button. */
  onRetry?: () => Promise<unknown> | void
}

export function RoleList({ roles, isLoading, error, onRetry }: RoleListProps): React.ReactElement {
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canUpdateRole = can('roles', 'update')
  const canDeleteRole = can('roles', 'delete')
  const canCloneRole = can('roles', 'create')
  const [roleToClone, setRoleToClone] = useState<Role | null>(null)
  const [openRoleActionsId, setOpenRoleActionsId] = useState<string | null>(null)
  const [roleToEdit, setRoleToEdit] = useState<Role | null>(null)
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null)

  const deleteMutation = useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.all })
      // Users holding the deleted role render its name/permissions.
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('Role deleted')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const handleDeleteClick = (role: Role): void => {
    setRoleToDelete(role)
  }

  const handleConfirmDelete = (): void => {
    if (roleToDelete) {
      deleteMutation.mutate(roleToDelete._id)
      setRoleToDelete(null)
    }
  }

  if (isLoading) {
    return (
      <SectionLoader variant="table" cols={5} rows={6} label="Loading roles" />
    )
  }

  if (error) {
    return (
      <QueryErrorState error={error} title="Couldn't load roles" onRetry={onRetry} />
    )
  }

  if (roles.length === 0) {
    return (
      <p className="rounded-lg border border-gray-200 bg-gray-50 py-8 text-center text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
        No roles yet. Create one below.
      </p>
    )
  }

  return (
    <>
      <DataTable>
        <DataTableHead>
          <tr>
            <DataTableHeadCell>Name</DataTableHeadCell>
            <DataTableHeadCell>Slug</DataTableHeadCell>
            <DataTableHeadCell>Scope</DataTableHeadCell>
            <DataTableHeadCell>Default</DataTableHeadCell>
            <DataTableHeadCell align="right">Actions</DataTableHeadCell>
          </tr>
        </DataTableHead>
        <DataTableBody>
          {roles.map((role) => (
            <DataTableRow key={role._id}>
              <DataTableCell className="font-medium">{role.name}</DataTableCell>
              <DataTableCell className="text-gray-600 dark:text-gray-400">{role.slug}</DataTableCell>
              <DataTableCell className="text-gray-700 dark:text-gray-300">
                {role.scope === 'all'
                  ? 'Org-wide'
                  : role.scope === 'company' || role.scope === 'team'
                    ? 'Company-scoped'
                    : 'Personal'}
              </DataTableCell>
              <DataTableCell className="text-gray-600 dark:text-gray-400">{role.isDefault ? 'Yes' : '—'}</DataTableCell>
              <DataTableCell align="right">
                {canUpdateRole || canCloneRole || canDeleteRole ? (
                  <TableRowActionsMenu
                    rowId={role._id}
                    openRowId={openRoleActionsId}
                    onOpenRowIdChange={setOpenRoleActionsId}
                    triggerAriaLabel={`Actions for ${role.name}`}
                    menuAriaLabel="Role actions"
                  >
                    {canUpdateRole && (
                      <TableRowActionsMenuItem
                        icon={<Pencil className="h-4 w-4" aria-hidden />}
                        onSelect={() => setRoleToEdit(role)}
                      >
                        Edit
                      </TableRowActionsMenuItem>
                    )}
                    {canCloneRole && (
                      <TableRowActionsMenuItem
                        icon={<Copy className="h-4 w-4" aria-hidden />}
                        onSelect={() => setRoleToClone(role)}
                      >
                        Clone
                      </TableRowActionsMenuItem>
                    )}
                    {canDeleteRole && (
                      <>
                        {canUpdateRole || canCloneRole ? <TableRowActionsMenuDivider /> : null}
                        <TableRowActionsMenuItem
                          variant="destructive"
                          icon={<Trash2 className="h-4 w-4" aria-hidden />}
                          onSelect={() => handleDeleteClick(role)}
                          disabled={deleteMutation.isPending || !!roleToDelete}
                        >
                          Delete
                        </TableRowActionsMenuItem>
                      </>
                    )}
                  </TableRowActionsMenu>
                ) : null}
              </DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>

      <Modal
        open={roleToEdit !== null}
        onClose={() => setRoleToEdit(null)}
        title={roleToEdit ? `Edit ${roleToEdit.name}` : ''}
        className="max-w-lg"
      >
        {roleToEdit && (
          <EditRoleForm
            role={roleToEdit}
            onSuccess={() => setRoleToEdit(null)}
            onCancel={() => setRoleToEdit(null)}
          />
        )}
      </Modal>

      <CloneRoleModal role={roleToClone} onClose={() => setRoleToClone(null)} />

      <ConfirmDialog
        open={roleToDelete !== null}
        onClose={() => setRoleToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete role"
        message={
          roleToDelete
            ? `Delete role "${roleToDelete.name}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={deleteMutation.isPending}
      />
    </>
  )
}

RoleList.displayName = 'RoleList'
